"use client";

import * as React from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFont } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { type Editor, isNodeSelection } from "@tiptap/react";

// --- Icons ---
import { faChevronDown } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
// --- UI Primitives ---
import {
  UnifiedButton,
  UnifiedButtonProps,
} from "@/components/tiptap-ui/unified-button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";
// --- Lib ---
import { isNodeInSchema } from "@/lib/tiptap-utils";

interface FontDropdownMenuProps extends Omit<UnifiedButtonProps, "type"> {
  editor?: Editor | null;
  fonts?: string[];
  hideWhenUnavailable?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export function FontDropdownMenu({
  editor: providedEditor,
  fonts = ["Normal", "Cursive", "Monospace", "Serif"],
  hideWhenUnavailable = false,
  onOpenChange,
  ...props
}: FontDropdownMenuProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const editor = useTiptapEditor(providedEditor);

  const headingInSchema = isNodeInSchema("heading", editor);

  const handleOnOpenChange = React.useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onOpenChange?.(open);
    },
    [onOpenChange],
  );

  const canSetFont = React.useCallback((): boolean => {
    if (!editor) return false;
    return fonts.some((font) => editor.can().setFontFamily(font));
  }, [editor, fonts]);

  const isDisabled = !canSetFont();

  const show = React.useMemo(() => {
    if (!headingInSchema) {
      return false;
    }

    if (hideWhenUnavailable) {
      if (isNodeSelection(editor?.state.selection)) {
        return false;
      }
    }

    return true;
  }, [headingInSchema, hideWhenUnavailable, editor]);

  const setTextStyle = React.useCallback(
    (font: string) => {
      if (!editor) return;
      if (font === "Normal") {
        editor.commands.unsetFontFamily();
      } else {
        editor.commands.setFontFamily(font);
      }
    },
    [editor],
  );

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOnOpenChange}>
      <DropdownMenuTrigger asChild>
        <UnifiedButton
          type="button"
          disabled={isDisabled}
          data-style="ghost"
          data-disabled={isDisabled}
          role="button"
          tabIndex={-1}
          aria-label="Set Font Family"
          tooltip="Text Style"
          {...props}
        >
          <FontAwesomeIcon icon={faFont} />
          <FontAwesomeIcon
            icon={faChevronDown}
            className="tiptap-button-dropdown-small"
          />
        </UnifiedButton>
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        <DropdownMenuGroup>
          {fonts.map((font) => (
            <DropdownMenuItem
              key={`font-${font}`}
              onClick={() => setTextStyle(font)}
            >
              <span className="tiptap-button-text">{font}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
